import React, { useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router";
import ReactStars from "react-rating-stars-component";
import { IoMdStar, IoMdStarOutline } from "react-icons/io";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { parsePhoneNumberFromString } from "libphonenumber-js";

import {
  LockIcon,
  SendRatingIcon,
  DetailEmailIcon,
  DetailPhoneIcon,
  DetailWhatsappIcon,
} from "../../assets/icons";
import { SUBSCRIPTION_STATUS } from "../../system";
import { EMPTY_STRING, SCREEN_PATH } from "../../constants";
import { formatRating } from "../../shared/utility";
import RoundedButton from "../../shared/components/buttons/RoundedButton";
import { agentActions } from "../../redux/actions";

const containerStyle = {
  width: "100%",
  height: "250px",
  borderRadius: "5px",
};

const options = {
  draggable: false,
  zoomControl: false,
  disableDefaultUI: true,
  disableDoubleClickZoom: true,
  clickableIcons: false,
  fullscreenControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  keyboardShortcuts: false,
};

const AgentDetails = ({
  agent,
  hasUserRole,
  location = {},
  giveAgentRating,
  subscriptionStatus,
  refetchDetails = () => {},
}) => {
  const {
    id,
    email,
    image,
    rating,
    firstName,
    lastName,
    phoneNumber,
    propertiesListed,
  } = agent || {};

  const navigate = useNavigate();
  const [agentRating, setAgentRating] = useState(0);
  const [showRatingComponent, setShowRatingComponent] = useState(false);

  const sendAgentRating = () => {
    if (agentRating === 0) return;
    giveAgentRating({ id, rating: agentRating }, () => {
      setShowRatingComponent(false);
      setAgentRating(0);
      refetchDetails();
    });
  };

  const navigateToSubscriptionPlansPage = () => {
    navigate(SCREEN_PATH.SUBSCRIPTION_PLANS, { replace: true });
  };

  const formatPhoneDisplay = (rawPhone) => {
    const phoneNumber = parsePhoneNumberFromString(rawPhone);
    return phoneNumber ? phoneNumber.formatInternational() : rawPhone;
  };

  const coordinates = {
    lat: location?.lat || 0,
    lng: location?.lng || 0,
  };

  return (
    <div className="agent-details-wrapper">
      <div className="row">
        <div className="col-12 col-lg-6 col-xl-7">
          <GoogleMap
            zoom={16}
            id="googleMap"
            options={options}
            mapContainerStyle={containerStyle}
            center={coordinates}
          >
            <Marker position={coordinates} />
          </GoogleMap>
        </div>
        <div className="col-12 col-lg-6 col-xl-5 agent-info-wrapper">
          <h3>About Agent</h3>
          {hasUserRole &&
          subscriptionStatus !== SUBSCRIPTION_STATUS.ACTIVE &&
          subscriptionStatus !== SUBSCRIPTION_STATUS.ONCANCELLED ? (
            <div className="agent-container">
              <img src={LockIcon} alt="lock" />
              <p className="subscribe-description">
                Please subscribe <br /> to view agent information
              </p>
              <div className="d-flex justify-content-center w-100 subscribe-button add-admin-button">
                <RoundedButton
                  className="mt-3 px-5 py-2"
                  onClick={() => navigateToSubscriptionPlansPage()}
                >
                  Subscribe
                </RoundedButton>
              </div>
            </div>
          ) : (
            <div className="d-flex column-gap-4 row-gap-3 align-items-start agent-details-info">
              <div className="agent-photo">
                <img src={image} alt="Agent" />
              </div>
              <div className="agent-details">
                <div className="agent-name line-clamp line-clamp-2">
                  {firstName + " " + lastName}
                </div>
                <div className="agent-phone">
                  <div className="d-flex align-items-center gap-2">
                    <div className="d-flex gap-2">
                      <img src={DetailPhoneIcon} alt={EMPTY_STRING} />
                      <img src={DetailWhatsappIcon} alt={EMPTY_STRING} />
                    </div>
                    <a href={`tel:+${phoneNumber}`}>
                      {formatPhoneDisplay(`+${phoneNumber}`)}
                    </a>
                  </div>
                </div>
                <div className="agent-email">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <img src={DetailEmailIcon} alt="" />
                    </div>
                    <a href={`mailto:${email}`}>{email}</a>
                  </div>
                </div>
                <div className="agent-rating">
                  {!showRatingComponent && (
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        Rating:
                        <span>{` ${formatRating(rating)}`}</span>
                      </div>
                      {hasUserRole && (
                        <div
                          className="rate-agent-text"
                          onClick={() => setShowRatingComponent(true)}
                        >
                          Rate Agent
                        </div>
                      )}
                    </div>
                  )}
                  {showRatingComponent && (
                    <div className="d-flex align-items-center gap-3">
                      <ReactStars
                        value={agentRating}
                        emptyIcon={<IoMdStarOutline />}
                        filledIcon={<IoMdStar />}
                        onChange={setAgentRating}
                      />
                      <img
                        src={SendRatingIcon}
                        alt=""
                        className="pointer"
                        onClick={sendAgentRating}
                      />
                    </div>
                  )}
                  <div>
                    Properties Listed: <span>{propertiesListed}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
  giveAgentRating: (data, func) =>
    dispatch(agentActions.giveAgentRating(data, func)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AgentDetails);
